import { Routes } from '@angular/router';
import { provideState } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { accountAccessGuard, loginGuard } from '@uc/shared/authentication';
import { orderProspectusGuard } from './order-prospectus/utils/order-prospectus.guard';
import { svgsGuard } from './svgs/svgs.guard';
import * as fromStateStatements from './statements/data-access/+state/statements.reducer';
import { StateStatementsEffects } from './statements/data-access/+state/statements.effects';
import { hecosSubjectsResolver } from './courses/guards/hecos-subjects.resolver';
import { StateRankingsCategoryEffects } from './rankings/data-access/+state/rankings-category.effects';
import * as stateRankingsReducer from './rankings/data-access/+state/rankings-category.reducer';
import * as stateCoursesReducer from './courses/data-access/+store/course-search.reducer';
import {
	CourseSearchFilterStateService,
	CourseSearchScrollerService,
	StateCourseSearchEffects,
} from './courses';
import { UniversitiesService } from './universities/services/universities.service';
import { UniversitiesStore } from './universities/data-access/+state/universities.store';
import { FilterTogglerService } from '@uc/web/core';
import { CopyGeneratorService } from './universities/services/copy-generator.service';
import * as fromStateAdvice from './advice/data-access/+state/advice.reducer';
import { StateAdviceEffects } from './advice/data-access/+state/advice.effects';
import * as fromStateGuides from './guides/data-access/+state/guides.reducer';
import { StateGuidesEffects } from './guides/data-access/+state/guides.effects';
import * as fromStateUniversity from './university/data-access/+state/university.reducer';
import { StateUniversityEffects } from './university/data-access/+state/university.effects';
import * as fromStateCampusLocations from './university/features/campus-locations/data-access/+state/campus-locations.reducer';
import { StateCampusLocationsEffects } from './university/features/campus-locations/data-access/+state/campus-locations.effects';
import * as fromStateAccommodations from './university/features/university-accommodation/data-access/+state/accommodation.reducer';
import { StateAccommodationsEffects } from './university/features/university-accommodation/data-access/+state/accommodation.effects';
import * as fromDegreeQuizState from './degree-quiz/data-access/+store/degree-quiz.reducer';
import { StateDegreeQuizEffects } from './degree-quiz/data-access/+store/degree-quiz.effects';
import { EventsStore } from './open-days/data-access/+state/open-days.store';

export const WEB_ROUTES: Routes = [
	// Unicompare Routes
	{
		path: '',
		loadChildren: () => import('./home').then((routes) => routes.HOME_ROUTES),
	},
	{
		path: 'universities',
		loadChildren: () =>
			import('./universities').then((routes) => routes.UNIVERSITIES_ROUTES),
		providers: [
			UniversitiesService,
			UniversitiesStore,
			FilterTogglerService,
			CopyGeneratorService,
		],
	},
	{
		path: 'universities/:uniName',
		providers: [
			provideState(
				fromStateUniversity._STATE_UNIVERSITY_FEATURE_KEY,
				fromStateUniversity.reducer,
			),
			provideState(
				fromStateCampusLocations._STATE_CAMPUS_LOCATIONS_FEATURE_KEY,
				fromStateCampusLocations.campusLocationsReducer,
			),
			provideState(
				fromStateAccommodations._STATE_ACCOMMODATIONS_FEATURE_KEY,
				fromStateAccommodations.accommodationsReducer,
			),
			provideEffects([
				StateUniversityEffects,
				StateCampusLocationsEffects,
				StateAccommodationsEffects,
			]),
		],
		loadChildren: () =>
			import('./university').then((routes) => routes.UNIVERSITY_ROUTES),
	},
	{
		path: 'universities/:uniSlug',
		loadChildren: () => import('./course').then((routes) => routes.COURSE_ROUTES),
	},
	{
		path: 'submit-review',
		loadChildren: () =>
			import('./submit-review').then((routes) => routes.SUBMIT_REVIEW_ROUTES),
	},
	{
		path: 'courses',
		resolve: { hecosSubjects: hecosSubjectsResolver },
		providers: [
			provideState(
				stateCoursesReducer._STATE_COURSE_SEARCH_FEATURE_KEY,
				stateCoursesReducer.reducer,
			),
			provideEffects([StateCourseSearchEffects]),
			CourseSearchFilterStateService,
			CourseSearchScrollerService,
			FilterTogglerService,
		],
		loadChildren: () => import('./courses').then((routes) => routes.COURSES_ROUTES),
	},
	{
		path: 'directory',
		loadChildren: () =>
			import('./directory').then((routes) => routes.DIRECTORY_ROUTES),
	},
	{
		path: 'open-days',
		providers: [EventsStore, FilterTogglerService],
		loadChildren: () =>
			import('./open-days').then((routes) => routes.OPEN_DAYS_ROUTES),
	},
	{
		path: 'advice',
		providers: [
			provideState(
				fromStateAdvice._STATE_ADVICE_FEATURE_KEY,
				fromStateAdvice.reducer,
			),
			provideEffects([StateAdviceEffects]),
		],
		loadChildren: () => import('./advice').then((routes) => routes.ADVICE_ROUTES),
	},
	{
		path: 'personal-statement-examples',
		providers: [
			provideState(
				fromStateStatements._STATE_STATEMENT_FEATURE_KEY,
				fromStateStatements.reducer,
			),
			provideEffects([StateStatementsEffects]),
		],
		loadChildren: () =>
			import('./statements/statements.routes').then(
				(routes) => routes.STATEMENT_ROUTES,
			),
	},
	{
		path: 'rankings',
		providers: [
			provideState(
				stateRankingsReducer._STATE_RANKINGS_CATEGORY_FEATURE_KEY,
				stateRankingsReducer.reducer,
			),
			provideEffects([StateRankingsCategoryEffects]),
		],
		loadChildren: () =>
			import('./rankings/rankings.routes').then((routes) => routes.RANKINGS_ROUTES),
	},
	{
		path: 'guides',
		providers: [
			provideState(
				fromStateGuides._STATE_GUIDES_FEATURE_KEY,
				fromStateGuides.reducer,
			),
			provideEffects([StateGuidesEffects]),
		],
		loadChildren: () => import('./guides').then((routes) => routes.GUIDES_ROUTES),
	},
	{
		path: 'user-profile',
		loadChildren: () =>
			import('./user-profile').then((routes) => routes.USER_PROFILE_ROUTES),
		canLoad: [accountAccessGuard],
	},
	{
		path: 'prospectus',
		loadChildren: () =>
			import('./select-prospectus').then(
				(routes) => routes.SELECT_PROSPECTUS_ROUTES,
			),
	},
	{
		path: 'order',
		loadChildren: () =>
			import('./order-prospectus').then((routes) => routes.ORDER_PROSPECTUS_ROUTES),
		canActivate: [orderProspectusGuard],
	},
	{
		path: 'select',
		loadChildren: () =>
			import('./order-prospectus-select').then(
				(routes) => routes.ORDER_PROSPECTUS_SELECT_ROUTES,
			),
	},
	{
		path: 'request',
		loadChildren: () =>
			import('./request-info').then((routes) => routes.REQUEST_INFO_ROUTES),
	},
	{
		path: 'register-interest',
		loadChildren: () =>
			import('./register-interest/register-interest.routes').then(
				(routes) => routes.REGISTER_INTEREST_ROUTES,
			),
	},
	{
		path: 'about',
		loadChildren: () => import('./about').then((routes) => routes.ABOUT_ROUTES),
	},
	{
		path: 'advertise',
		loadChildren: () =>
			import('./advertise').then((routes) => routes.ADVERTISE_ROUTES),
	},
	{
		path: 'contact',
		loadChildren: () => import('./contact').then((routes) => routes.CONTACT_ROUTES),
	},

	// Parent path for login, register and password so that LoginGuardService will only load once.
	{
		path: '',
		canActivate: [loginGuard],
		children: [
			{
				path: 'login',
				loadChildren: () =>
					import('./login').then((routes) => routes.LOGIN_ROUTES),
			},
			{
				path: 'register',
				loadChildren: () =>
					import('./register').then((routes) => routes.REGISTER_ROUTES),
			},
			{
				path: 'password',
				loadChildren: () =>
					import('./reset-password').then(
						(routes) => routes.RESET_PASSWORD_ROUTES,
					),
			},
		],
	},
	{
		path: 'degree-quiz',
		providers: [
			provideState(
				fromDegreeQuizState._STATE_DEGREE_QUIZ_FEATURE_KEY,
				fromDegreeQuizState.reducer,
			),
			provideEffects([StateDegreeQuizEffects]),
		],
		loadChildren: () =>
			import('./degree-quiz').then((routes) => routes.DEGREE_QUIZ_ROUTES),
	},
	{
		path: 'terms',
		loadChildren: () => import('./terms').then((routes) => routes.TERMS_ROUTES),
	},
	{
		path: 'advertising-terms',
		loadChildren: () =>
			import('./advertising-terms').then(
				(routes) => routes.ADVERTISING_TERMS_ROUTES,
			),
	},
	{
		path: 'privacy',
		loadChildren: () => import('./privacy').then((routes) => routes.PRIVACY_ROUTES),
	},
	{
		path: 'sitemap',
		loadChildren: () =>
			import('./sitemap/sitemap.routes').then((routes) => routes.SITEMAP_ROUTES),
	},
	{
		path: '404',
		loadChildren: () =>
			import('./not-found').then((routes) => routes.NOT_FOUND_ROUTES),
	},
	{
		path: 'svgs',
		loadChildren: () =>
			import('./svgs/svgs.routes').then((routes) => routes.SVGS_ROUTES),
		canLoad: [svgsGuard],
	},
	{ path: '**', redirectTo: '404' },
];
